"use client";

import { useState } from "react";

export type Billing = "monthly" | "yearly";

/**
 * The monthly/yearly switch on /product/crm, and the scope its answer applies
 * to.
 *
 * The rest of that page is Server Components, and the cards inside this one
 * stay that way: `children` is rendered on the server and passed straight
 * through, so the only thing that ships to the browser is the switch and the
 * `data-billing` attribute it writes. Each card prints both prices and hides
 * one with `group-data-[billing=…]` — a few dozen bytes of markup instead of a
 * client-side re-render of three plans, and both figures are in the HTML a
 * crawler reads either way.
 *
 * `aria-pressed` rather than tab roles, matching the home page's switch: the
 * buttons don't own the region below them and plain buttons don't promise the
 * arrow-key navigation tabs do.
 */
export default function BillingScope({
  label,
  monthly,
  yearly,
  children,
}: Readonly<{
  label: string;
  monthly: string;
  yearly: string;
  children: React.ReactNode;
}>) {
  const [billing, setBilling] = useState<Billing>("monthly");
  const options: { id: Billing; label: string }[] = [
    { id: "monthly", label: monthly },
    { id: "yearly", label: yearly },
  ];

  return (
    <div
      data-billing={billing}
      className="group/billing flex w-full flex-col items-center gap-8"
    >
      <div
        role="group"
        aria-label={label}
        className="grid grid-flow-col auto-cols-fr rounded-full border border-[#E6E6E6] bg-white p-0.5"
      >
        {options.map((option) => {
          const selected = option.id === billing;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={selected}
              onClick={() => setBilling(option.id)}
              className={`rounded-full px-5 py-1.5 text-[14px]/[20px] font-semibold transition-colors duration-300 select-none ${
                selected ? "bg-primary-strong text-white" : "text-black/60"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {children}
    </div>
  );
}
